import {
  countryOptionLabel,
  locationBelongsToCountry,
  normalizeCountry,
} from '../../../utils/countryCatalog.js';
import { getLocationTypeLabel } from '../../../constants/orgReference.js';

function cleanLocationPart(value) {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

/** US names like "Pensacola, FL" / "Ladson, SC" when city/state columns are empty. */
const US_CITY_STATE_NAME = /^(.+),\s*([A-Za-z]{2})$/;

/**
 * City + state for a location, inferring from "City, ST" names when columns are empty.
 * @param {object|null|undefined} location
 * @returns {{ city: string, state: string }}
 */
export function resolveLocationCityState(location) {
  if (!location || typeof location !== 'object') return { city: '', state: '' };
  const city = cleanLocationPart(location.city);
  const state = cleanLocationPart(location.state);
  if (city || state) return { city, state };

  const name = cleanLocationPart(location.name || location.label);
  if (!name || normalizeCountry(location.country) !== 'US') {
    return { city: '', state: '' };
  }
  const match = name.match(US_CITY_STATE_NAME);
  if (!match) return { city: '', state: '' };
  return { city: cleanLocationPart(match[1]), state: match[2].toUpperCase() };
}

/**
 * City, state, country line for a location row.
 * @param {object|null|undefined} location
 * @returns {string}
 */
export function formatLocationPlace(location) {
  if (!location || typeof location !== 'object') return '';
  const { city, state } = resolveLocationCityState(location);
  const parts = [city, state, countryOptionLabel(location.country)].filter(Boolean);
  return parts.join(', ');
}

/**
 * Address line for location selects and summaries (street, place, country, type).
 * @param {object|null|undefined} location
 * @returns {string}
 */
export function formatLocationMeta(location) {
  if (!location || typeof location !== 'object') return '';
  const street = cleanLocationPart(location.address);
  const place = formatLocationPlace(location);
  const typeKey = cleanLocationPart(location.type).toLowerCase();
  const typeLabel = typeKey && typeKey !== 'other' ? getLocationTypeLabel(location.type) : '';
  const addressLine = [street, place].filter(Boolean).join(', ');
  return [addressLine, typeLabel].filter(Boolean).join(' · ');
}

/**
 * Extra detail for read-only panels (same address line as selects).
 * @param {object|null|undefined} location
 * @returns {string}
 */
export function formatLocationDetails(location) {
  return formatLocationMeta(location);
}

/**
 * Normalize a reference option id for Select binding and comparisons.
 * @param {unknown} id
 * @returns {number|null}
 */
export function normalizeReferenceId(id) {
  if (id === null || id === undefined || id === '') return null;
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Location option for Select bindings that use either id/name or value/label.
 * Keeps catalog fields (type, country, city, address) for the edit modal.
 * @param {object} row
 * @returns {object}
 */
export function shapeLocationOption(row) {
  if (!row || typeof row !== 'object') return row;
  const id = normalizeReferenceId(row.id ?? row.value);
  const name = String(row.name || row.label || '').trim();
  const shaped = {
    ...row,
    id,
    name,
    label: name,
    value: id,
  };
  const { city, state } = resolveLocationCityState(shaped);
  const meta = formatLocationMeta(shaped);
  return {
    ...shaped,
    meta: meta || null,
    searchText: [name, city, state, shaped.country, shaped.address, shaped.type, meta]
      .map((part) => String(part ?? '').trim())
      .filter(Boolean)
      .join(' '),
  };
}

/**
 * Case-insensitive trimmed name for duplicate checks.
 * @param {unknown} name
 * @returns {string}
 */
export function normalizeReferenceName(name) {
  return String(name ?? '')
    .trim()
    .toLowerCase();
}

/**
 * Upsert an option into a mutable array by id (string-safe).
 * @param {Array<object>} options
 * @param {object} item
 * @returns {object} the upserted item
 */
export function upsertOption(options, item) {
  if (!Array.isArray(options) || !item) return item;
  const id = normalizeReferenceId(item.id ?? item.value);
  if (id == null) {
    options.push(item);
    return item;
  }
  const index = options.findIndex(
    (o) => String(o?.id ?? o?.value) === String(id)
  );
  const next = { ...item, id };
  if (index === -1) {
    options.push(next);
  } else {
    options[index] = { ...options[index], ...next };
  }
  return next;
}

/**
 * Remove an option from a mutable array by id.
 * @param {Array<object>} options
 * @param {unknown} idOrItem
 * @returns {boolean}
 */
export function removeOption(options, idOrItem) {
  if (!Array.isArray(options)) return false;
  const id = normalizeReferenceId(
    idOrItem && typeof idOrItem === 'object'
      ? idOrItem.id ?? idOrItem.value
      : idOrItem
  );
  if (id == null) return false;
  const index = options.findIndex(
    (row) => String(row?.id ?? row?.value) === String(id)
  );
  if (index === -1) return false;
  options.splice(index, 1);
  return true;
}

/**
 * Find an option whose name matches (trim + case-insensitive).
 * @param {Array<object>} options
 * @param {string} name
 * @param {string} [nameKey='name']
 * @returns {object|null}
 */
export function findOptionByNormalizedName(options, name, nameKey = 'name') {
  const target = normalizeReferenceName(name);
  if (!target || !Array.isArray(options)) return null;
  return options.find((o) => normalizeReferenceName(o?.[nameKey]) === target) ?? null;
}

/** UI hints for department-scoped position selects. */
export const POSITION_SELECT_HINTS = {
  noDepartment: 'Select a department first.',
  noPositions: 'No positions in this department yet. Use + to create one. Rename or delete titles in Administration → Positions.',
  catalogManage: 'This list adds a new title only. Rename or delete in Administration → Positions.',
};

/**
 * Whether a sub-department/team belongs to the given department.
 * Rows with null department_id are treated as org-wide.
 * @param {number|null} itemId
 * @param {number|null} departmentId
 * @param {Array<object>} list
 * @param {string} [deptKey='department_id']
 * @returns {boolean}
 */
export function belongsToDepartment(itemId, departmentId, list, deptKey = 'department_id') {
  const id = normalizeReferenceId(itemId);
  if (id == null) return true;
  if (!Array.isArray(list)) return false;
  const row = list.find((o) => String(o?.id ?? o?.value) === String(id));
  if (!row) return false;
  const rowDept = normalizeReferenceId(row[deptKey]);
  if (rowDept == null) return true;
  const dept = normalizeReferenceId(departmentId);
  return dept != null && String(rowDept) === String(dept);
}

/**
 * Whether a position belongs to the selected department (strict — excludes global positions).
 * @param {number|null} positionId
 * @param {number|null} departmentId
 * @param {Array<object>} list
 * @returns {boolean}
 */
export function positionBelongsToDepartment(positionId, departmentId, list) {
  const id = normalizeReferenceId(positionId);
  if (id == null) return true;
  const dept = normalizeReferenceId(departmentId);
  if (dept == null) return false;
  if (!Array.isArray(list)) return false;
  const row = list.find((o) => String(o?.id ?? o?.value) === String(id));
  if (!row) return false;
  const rowDept = normalizeReferenceId(row.department_id);
  return rowDept != null && String(rowDept) === String(dept);
}

/**
 * Positions for a department-scoped select (strict match on department_id).
 * @param {Array<object>} positions
 * @param {number|null} departmentId
 * @param {object} [options]
 * @param {number|null} [options.keepPositionId]
 * @param {string} [options.fallbackLabel]
 * @param {object|null} [options.pendingCreated]
 * @param {(id: unknown) => number|null} [options.normalizeId]
 * @param {string} [options.idKey='id']
 * @param {string} [options.nameKey='name']
 * @returns {Array<object>}
 */
export function filterPositionsByDepartment(positions, departmentId, options = {}) {
  const dept = normalizeReferenceId(departmentId);
  if (dept == null) return [];

  const source = Array.isArray(positions) ? positions : [];
  const normalizeId = options.normalizeId ?? normalizeReferenceId;
  const idKey = options.idKey ?? 'id';
  const nameKey = options.nameKey ?? 'name';

  const normalizePosition = (position) => {
    const id = normalizeId(position?.[idKey] ?? position?.id ?? position?.value);
    return {
      ...position,
      [idKey]: id,
      department_id: normalizeReferenceId(position?.department_id),
    };
  };

  let result = source
    .filter((position) => {
      const rowDept = normalizeReferenceId(position?.department_id);
      return rowDept != null && String(rowDept) === String(dept);
    })
    .map(normalizePosition);

  const keepId = normalizeId(options.keepPositionId);
  if (keepId != null && !result.some((position) => normalizeId(position[idKey]) === keepId)) {
    const fallback = source.find(
      (position) => normalizeId(position?.[idKey] ?? position?.id ?? position?.value) === keepId
    );
    if (fallback) {
      result = [normalizePosition(fallback), ...result];
    } else if (options.fallbackLabel) {
      result = [
        {
          [idKey]: keepId,
          [nameKey]: options.fallbackLabel,
          department_id: dept,
        },
        ...result,
      ];
    }
  }

  const pending = options.pendingCreated;
  if (pending) {
    const pendingId = normalizeId(pending?.[idKey] ?? pending?.id ?? pending?.value);
    const pendingDept = normalizeReferenceId(pending?.department_id);
    if (
      pendingId != null &&
      pendingDept != null &&
      String(pendingDept) === String(dept) &&
      !result.some((position) => normalizeId(position[idKey]) === pendingId)
    ) {
      result = [normalizePosition(pending), ...result];
    }
  }

  return result;
}

/**
 * Hint text for a department-scoped position select.
 * @param {number|null|undefined} departmentId
 * @param {Array<object>} options
 * @returns {string}
 */
export function positionSelectHint(departmentId, options) {
  if (!normalizeReferenceId(departmentId)) {
    return POSITION_SELECT_HINTS.noDepartment;
  }
  if (!Array.isArray(options) || options.length === 0) {
    return POSITION_SELECT_HINTS.noPositions;
  }
  return POSITION_SELECT_HINTS.catalogManage;
}

/** UI hints for department-scoped team selects. */
export const TEAM_SELECT_HINTS = {
  noDepartment: 'Select a department first.',
};

export const EMPLOYEE_ORG_PLACEMENT_MESSAGES = {
  positionNeedsDepartment: 'Department is required when position is selected',
  subNeedsDepartment: 'Select a department before assigning a sub-department.',
  teamNeedsDepartment: 'Select a department before assigning a team.',
  subWrongDepartment: 'Sub-department must belong to the selected department.',
  teamWrongDepartment: 'Team must belong to the selected department.',
  teamWrongSub:
    'Team must match the selected sub-department (or clear the sub-department).',
};

/**
 * Draft field names for create (camelCase) vs edit (snake_case).
 * @param {object|null|undefined} draft
 * @returns {{ department: string, position: string, subDepartment: string, team: string }}
 */
export function resolveOrgDraftKeys(draft) {
  if (
    draft &&
    typeof draft === 'object' &&
    (Object.prototype.hasOwnProperty.call(draft, 'departmentId') ||
      Object.prototype.hasOwnProperty.call(draft, 'positionId') ||
      Object.prototype.hasOwnProperty.call(draft, 'subDepartmentId') ||
      Object.prototype.hasOwnProperty.call(draft, 'teamId'))
  ) {
    return {
      department: 'departmentId',
      position: 'positionId',
      subDepartment: 'subDepartmentId',
      team: 'teamId',
    };
  }
  return {
    department: 'department_id',
    position: 'position_id',
    subDepartment: 'sub_department_id',
    team: 'team_id',
  };
}

function findReferenceRow(list, id) {
  const target = normalizeReferenceId(id);
  if (target == null || !Array.isArray(list)) return null;
  return (
    list.find((row) => normalizeReferenceId(row?.id ?? row?.value) === target) || null
  );
}

function keepCurrentReference(source, keepId, normalizeRow, normalizeId) {
  const id = normalizeId(keepId);
  if (id == null) return [];
  const fallback = source.find(
    (row) => normalizeId(row?.id ?? row?.value) === id
  );
  return fallback ? [normalizeRow(fallback)] : [];
}

/**
 * Sub-department requires a department (backend: sub_department_id requires department_id).
 * @param {number|null} subDepartmentId
 * @param {number|null} departmentId
 * @param {Array<object>} list
 * @returns {boolean}
 */
export function subDepartmentMatchesPlacement(subDepartmentId, departmentId, list) {
  const id = normalizeReferenceId(subDepartmentId);
  if (id == null) return true;
  if (normalizeReferenceId(departmentId) == null) return false;
  if (!Array.isArray(list) || list.length === 0) return true;
  if (!findReferenceRow(list, id)) return true;
  return belongsToDepartment(subDepartmentId, departmentId, list);
}

/**
 * Team requires a department; if a sub-department is set, the team must use that sub.
 * Matches manageProfile.php org-placement checks.
 * @param {number|null} teamId
 * @param {{ departmentId?: number|null, subDepartmentId?: number|null }} placement
 * @param {Array<object>} teams
 * @returns {boolean}
 */
export function teamMatchesPlacement(teamId, placement = {}, teams) {
  const id = normalizeReferenceId(teamId);
  if (id == null) return true;
  const departmentId = normalizeReferenceId(placement.departmentId);
  if (departmentId == null) return false;
  if (!Array.isArray(teams) || teams.length === 0) return true;
  const row = findReferenceRow(teams, id);
  if (!row) return true;
  const rowDept = normalizeReferenceId(row.department_id);
  if (rowDept != null && rowDept !== departmentId) return false;
  const subDepartmentId = normalizeReferenceId(placement.subDepartmentId);
  if (subDepartmentId == null) return true;
  const rowSub = normalizeReferenceId(row.sub_department_id);
  return rowSub != null && rowSub === subDepartmentId;
}

/**
 * Team is compatible with the draft department + sub-department.
 * Accepts create (camelCase) and edit (snake_case) drafts.
 * @param {number|null} teamId
 * @param {object|null|undefined} draft
 * @param {Array<object>} teams
 * @returns {boolean}
 */
export function teamMatchesContext(teamId, draft, teams) {
  const keys = resolveOrgDraftKeys(draft);
  return teamMatchesPlacement(
    teamId,
    {
      departmentId: draft?.[keys.department],
      subDepartmentId: draft?.[keys.subDepartment],
    },
    teams
  );
}

/**
 * Sub-departments for a department-scoped select.
 * Empty department → no options (keepId stays visible).
 * @param {Array<object>} subDepartments
 * @param {unknown} departmentId
 * @param {{ keepId?: unknown, normalizeId?: Function }} [options]
 * @returns {Array<object>}
 */
export function filterSubDepartmentsByDepartment(subDepartments, departmentId, options = {}) {
  const source = Array.isArray(subDepartments) ? subDepartments : [];
  const normalizeId = options.normalizeId ?? normalizeReferenceId;
  const dept = normalizeReferenceId(departmentId);
  const normalizeRow = (row) => ({
    ...row,
    id: normalizeId(row?.id ?? row?.value),
    department_id: normalizeId(row?.department_id),
  });

  if (dept == null) {
    return keepCurrentReference(source, options.keepId, normalizeRow, normalizeId);
  }

  let result = source
    .filter((row) => {
      const rowDept = normalizeId(row?.department_id);
      return rowDept == null || rowDept === dept;
    })
    .map(normalizeRow);

  const keepId = normalizeId(options.keepId);
  if (keepId != null && !result.some((row) => normalizeId(row.id) === keepId)) {
    result = [...keepCurrentReference(source, keepId, normalizeRow, normalizeId), ...result];
  }
  return result;
}

/**
 * Teams for department (+ optional sub-department) selects.
 * Empty department → no options (keepId stays visible).
 * @param {Array<object>} teams
 * @param {{ departmentId?: unknown, subDepartmentId?: unknown }} placement
 * @param {{ keepId?: unknown, normalizeId?: Function }} [options]
 * @returns {Array<object>}
 */
export function filterTeamsByPlacement(teams, placement = {}, options = {}) {
  const source = Array.isArray(teams) ? teams : [];
  const normalizeId = options.normalizeId ?? normalizeReferenceId;
  const departmentId = normalizeReferenceId(placement.departmentId);
  const subDepartmentId = normalizeReferenceId(placement.subDepartmentId);
  const normalizeRow = (row) => ({
    ...row,
    id: normalizeId(row?.id ?? row?.value),
    department_id: normalizeId(row?.department_id),
    sub_department_id: normalizeId(row?.sub_department_id),
  });

  if (departmentId == null) {
    return keepCurrentReference(source, options.keepId, normalizeRow, normalizeId);
  }

  let result = source
    .filter((row) => {
      const rowDept = normalizeId(row?.department_id);
      if (rowDept != null && rowDept !== departmentId) return false;
      if (subDepartmentId == null) return true;
      return normalizeId(row?.sub_department_id) === subDepartmentId;
    })
    .map(normalizeRow);

  const keepId = normalizeId(options.keepId);
  if (keepId != null && !result.some((row) => normalizeId(row.id) === keepId)) {
    result = [...keepCurrentReference(source, keepId, normalizeRow, normalizeId), ...result];
  }
  return result;
}

/**
 * @param {unknown} departmentId
 * @returns {string}
 */
export function teamSelectHint(departmentId) {
  if (!normalizeReferenceId(departmentId)) return TEAM_SELECT_HINTS.noDepartment;
  return '';
}

/**
 * Frontend errors matching manageProfile/manageEmployees org-placement rules.
 * @param {object} draft
 * @param {{ positions?: Array, subDepartments?: Array, teams?: Array }} [options]
 * @returns {Array<{ field: 'position'|'subDepartment'|'team', message: string }>}
 */
export function collectEmployeeOrgPlacementErrors(draft, options = {}) {
  const keys = resolveOrgDraftKeys(draft);
  const departmentId = draft?.[keys.department];
  const positionId = draft?.[keys.position];
  const subDepartmentId = draft?.[keys.subDepartment];
  const teamId = draft?.[keys.team];
  const positions = options.positions ?? [];
  const subDepartments = options.subDepartments ?? [];
  const teams = options.teams ?? [];
  const optionsReady = options.optionsReady ?? {};
  const positionsReady = optionsReady.positions !== false;
  const subDepartmentsReady = optionsReady.subDepartments !== false;
  const teamsReady = optionsReady.teams !== false;
  const errors = [];

  if (normalizeReferenceId(positionId) && !normalizeReferenceId(departmentId)) {
    errors.push({
      field: 'position',
      message: EMPLOYEE_ORG_PLACEMENT_MESSAGES.positionNeedsDepartment,
    });
  } else if (normalizeReferenceId(positionId) && normalizeReferenceId(departmentId)) {
    if (positions.length) {
      if (!positionBelongsToDepartment(positionId, departmentId, positions)) {
        errors.push({
          field: 'position',
          message: 'Position does not belong to the selected department.',
        });
      }
    } else if (positionsReady) {
      errors.push({
        field: 'position',
        message: 'Selected position is not available for this department.',
      });
    }
  }

  if (normalizeReferenceId(subDepartmentId) && !normalizeReferenceId(departmentId)) {
    errors.push({
      field: 'subDepartment',
      message: EMPLOYEE_ORG_PLACEMENT_MESSAGES.subNeedsDepartment,
    });
  } else if (normalizeReferenceId(subDepartmentId) && normalizeReferenceId(departmentId)) {
    if (subDepartments.length) {
      if (!subDepartmentMatchesPlacement(subDepartmentId, departmentId, subDepartments)) {
        errors.push({
          field: 'subDepartment',
          message: EMPLOYEE_ORG_PLACEMENT_MESSAGES.subWrongDepartment,
        });
      }
    } else if (subDepartmentsReady) {
      errors.push({
        field: 'subDepartment',
        message: 'Selected sub-department is not available for this department.',
      });
    }
  }

  if (normalizeReferenceId(teamId) && !normalizeReferenceId(departmentId)) {
    errors.push({
      field: 'team',
      message: EMPLOYEE_ORG_PLACEMENT_MESSAGES.teamNeedsDepartment,
    });
  } else if (normalizeReferenceId(teamId) && normalizeReferenceId(departmentId)) {
    if (teams.length) {
      if (!teamMatchesPlacement(teamId, { departmentId, subDepartmentId: null }, teams)) {
        errors.push({
          field: 'team',
          message: EMPLOYEE_ORG_PLACEMENT_MESSAGES.teamWrongDepartment,
        });
      } else if (!teamMatchesPlacement(teamId, { departmentId, subDepartmentId }, teams)) {
        errors.push({
          field: 'team',
          message: EMPLOYEE_ORG_PLACEMENT_MESSAGES.teamWrongSub,
        });
      }
    } else if (teamsReady) {
      errors.push({
        field: 'team',
        message: 'Selected team is not available for this placement.',
      });
    }
  }

  return errors;
}

/**
 * Locations for a country-scoped select.
 * Empty country → no options. keepId stays visible when it is outside the filter.
 *
 * @param {Array<object>} locations
 * @param {unknown} country
 * @param {object} [options]
 * @param {unknown} [options.keepId]
 * @param {(id: unknown) => number|null} [options.normalizeId]
 * @param {string} [options.idKey='id']
 * @returns {Array<object>}
 */
export function filterLocationsByCountry(locations, country, options = {}) {
  const source = Array.isArray(locations) ? locations : [];
  const normalizeId = options.normalizeId ?? normalizeReferenceId;
  const idKey = options.idKey ?? 'id';
  const selectedCountry = normalizeCountry(country);

  const normalizeRow = (row) => ({
    ...row,
    [idKey]: normalizeId(row?.[idKey] ?? row?.id ?? row?.value),
  });

  let result = selectedCountry
    ? source.filter((row) => locationBelongsToCountry(row, selectedCountry)).map(normalizeRow)
    : [];

  const keepId = normalizeId(options.keepId);
  if (keepId != null && !result.some((row) => normalizeId(row[idKey]) === keepId)) {
    const fallback = source.find(
      (row) => normalizeId(row?.[idKey] ?? row?.id ?? row?.value) === keepId
    );
    if (fallback) {
      result = [normalizeRow(fallback), ...result];
    }
  }

  return result;
}

/**
 * Clear incompatible org FKs after department or sub-department change.
 * Mutates draft in place. Supports create (camelCase) and edit (snake_case) drafts.
 * @param {object} draft
 * @param {{ positions?: Array, subDepartments?: Array, teams?: Array }} options
 */
export function clearInvalidDependentReferences(draft, options = {}) {
  if (!draft) return;
  const keys = resolveOrgDraftKeys(draft);
  const positions = options.positions ?? [];
  const subDepartments = options.subDepartments ?? [];
  const teams = options.teams ?? [];
  const departmentId = draft[keys.department];

  if (!positionBelongsToDepartment(draft[keys.position], departmentId, positions)) {
    draft[keys.position] = null;
  }
  if (!subDepartmentMatchesPlacement(draft[keys.subDepartment], departmentId, subDepartments)) {
    draft[keys.subDepartment] = null;
  }
  if (
    !teamMatchesPlacement(
      draft[keys.team],
      { departmentId, subDepartmentId: draft[keys.subDepartment] },
      teams
    )
  ) {
    draft[keys.team] = null;
  }
}
